import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getToken } from '../../api/client';
import { jobsApi } from '../../api/jobs';
import {
  workspacesApi,
  setWorkspaceId,
  type WorkspaceInfo,
} from '../../api/workspaces';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export default function WorkspacesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCompanyCode, setNewCompanyCode] = useState('');

  // The ADMIN management page lists EVERYTHING (AdminOnly endpoint) — the
  // daily switcher's /workspaces/mine is membership-only for every role now.
  // with-members carries WHO each book belongs to (kas klientas) and includes
  // soft-removed rows (greyed, with Atkurti).
  const { data: rows, isLoading } = useQuery({
    queryKey: ['workspaces-all'],
    queryFn: workspacesApi.listAllWithMembers,
  });
  const workspaces = rows?.map(r => r.workspace) ?? [];

  // Nightly-sync job health (in-memory snapshot — resets on API restart).
  const { data: jobStatuses } = useQuery({
    queryKey: ['admin-jobs'],
    queryFn: jobsApi.list,
    refetchInterval: 30_000,
    retry: false,
  });
  const syncJob = jobStatuses?.find(j => j.jobName === 'WorkspaceSyncJob');

  const [deletingWorkspaceName, setDeletingWorkspaceName] = useState('');

  const deleteMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(); // all: this page reads workspaces-all, the switcher reads workspaces
      if (deletingWorkspaceName) {
        addToast(`Darbo sritis „${deletingWorkspaceName}“ ištrinta`, 'success');
        setDeletingWorkspaceName('');
      }
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.restore(id),
    onSuccess: () => {
      addToast('Darbo sritis atkurta', 'success');
      queryClient.invalidateQueries();
    },
    onError: (err: Error) => addToast(err.message || 'Nepavyko atkurti', 'error'),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.activate(id),
    onSuccess: (ws: WorkspaceInfo) => {
      setWorkspaceId(ws.id);
      queryClient.invalidateQueries();
    },
    onError: (err: Error) =>
      addToast(err.message || 'Nepavyko aktyvuoti darbo srities', 'error'),
  });

  const toggleAiMutation = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      workspacesApi.update(id, { aiEgressDisabled: disabled }),
    onSuccess: () => {
      queryClient.invalidateQueries(); // all: this page reads workspaces-all, the switcher reads workspaces
    },
  });

  // ADR-010/012 write-back config (mirror workspaces only): the credential
  // link that lets drafts be registered in the client's Apskaita5 book.
  const [writebackEditId, setWritebackEditId] = useState<string | null>(null);
  const [writebackForm, setWritebackForm] = useState({ slug: '', appName: 'a5suite', token: '' });

  const openWritebackEditor = (ws: WorkspaceInfo) => {
    setWritebackEditId(ws.id);
    setWritebackForm({ slug: ws.writebackSlug ?? '', appName: 'a5suite', token: '' });
  };

  const setWritebackMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { slug: string; appName: string; token: string } }) =>
      workspacesApi.setWriteback(id, data),
    onSuccess: (_void, { data }) => {
      setWritebackEditId(null);
      queryClient.invalidateQueries(); // badge + switcher read the same workspace data
      addToast(data.slug.trim() ? 'Registravimas į kliento knygą įjungtas' : 'Registravimas į kliento knygą išjungtas', 'success');
    },
    onError: (err: Error) => addToast(err.message || 'Nepavyko išsaugoti registravimo nustatymų', 'error'),
  });

  const handleDisableWriteback = (id: string) => {
    if (!window.confirm('Išjungti registravimą į kliento Apskaita5 knygą šiai darbo sričiai?')) return;
    setWritebackMutation.mutate({ id, data: { slug: '', appName: '', token: '' } });
  };

  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const handleSyncNow = async (id: string) => {
    setSyncingId(id);
    try {
      const r = await workspacesApi.syncNow(id);
      addToast(`Sinchronizuota: ${r.recordsImported} įrašai (${Math.round(r.durationMs / 100) / 10} s)`, 'success');
      queryClient.invalidateQueries(); // all: this page reads workspaces-all, the switcher reads workspaces
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Sinchronizacija nepavyko', 'error');
    } finally {
      setSyncingId(null);
    }
  };

  // Sequential (each sync is a full import; the endpoint is rate-limited).
  // Server-driven: the endpoint sweeps the MySQL catalog (creating missing
  // workspaces for EVERY company DB on the cloud) then syncs them all.
  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const r = await workspacesApi.syncAll();
      const summary = r.workspacesCreated > 0
        ? `Sukurta: ${r.workspacesCreated}, sinchronizuota: ${r.synced}`
        : `Sinchronizuota: ${r.synced}`;
      addToast(r.failures > 0 ? `${summary}, klaidos: ${r.failures}` : summary,
               r.failures > 0 ? 'error' : 'success');
      queryClient.invalidateQueries(); // all: this page reads workspaces-all, the switcher reads workspaces
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Sinchronizacija nepavyko', 'error');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newCompanyCode.trim()) return;
    setCreating(true);
    try {
      const ws = await workspacesApi.create({
        name: newName.trim(),
        companyName: newName.trim(),
        companyCode: newCompanyCode.trim(),
      });
      await activateMutation.mutateAsync(ws.id);
      setNewName('');
      setNewCompanyCode('');
      queryClient.invalidateQueries(); // all: this page reads workspaces-all, the switcher reads workspaces
      navigate('/');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Nepavyko sukurti darbo srities');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (ws: WorkspaceInfo) => {
    if (!confirm(`Pašalinti „${ws.name}“ iš a5suite? Duomenys išlieka ir atnaujinami — bet kada galima Atkurti.`)) return;
    setDeletingWorkspaceName(ws.name);
    deleteMutation.mutate(ws.id);
  };

  const handleExport = (ws: WorkspaceInfo) => {
    // Download via authenticated fetch (cookie + Bearer when in memory)
    const token = getToken();
    const url = `${import.meta.env.VITE_API_URL ?? '/api'}/workspaces/${ws.id}/export`;
    fetch(url, {
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Eksportas nepavyko');
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${ws.name}.db`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => alert(err.message));
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold text-gray-900">Darbo sritys</h1>

      {/* Create new company — a workspace exists ONLY as a client company
          (imported db or a fresh company). The I_Kodas code is what ties the
          workspace to the client's shop account via the membership join. */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 mb-3">Nauja įmonė</h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="Įmonės pavadinimas"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <input
            type="text"
            className="w-44 px-3 py-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="Įmonės kodas"
            value={newCompanyCode}
            onChange={(e) => setNewCompanyCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={handleCreate}
            disabled={creating || !newName.trim() || !newCompanyCode.trim()}
          >
            {creating ? 'Kuriama…' : 'Sukurti'}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Sukuriama nauja įmonės darbo sritis. Kodas turi sutapti su kliento įmonės kodu —
          juo kliento paskyrai suteikiamas prisijungimas.
        </p>
      </div>

      {/* Workspace list */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <h2 className="font-semibold text-gray-900">Visos darbo sritys</h2>
          {/* Nightly-sync job health (green ✓ / red with error / amber >30h or not-yet-run) */}
          {syncJob && (
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full border ${
                !syncJob.succeeded
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : Date.now() - new Date(syncJob.lastRunUtc).getTime() > 30 * 3600_000
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-green-50 text-green-700 border-green-200'
              }`}
              title={syncJob.errorMessage ?? undefined}
            >
              {syncJob.succeeded ? '✓' : '!'} Naktinis sinchronizavimas: {new Date(syncJob.lastRunUtc).toLocaleString('lt-LT')}
            </span>
          )}
          <div className="flex-1" />
          {(workspaces ?? []).some(ws => ws.sourceKind === 'mysql') && (
            <button
              className="px-3 py-1.5 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:border-primary-400 disabled:opacity-50"
              onClick={handleSyncAll}
              disabled={syncingAll || syncingId !== null}
            >
              {syncingAll ? 'Sinchronizuojama…' : 'Sinchronizuoti visus'}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-left">Pavadinimas</th>
                <th className="text-left">Sukurta</th>
                <th className="text-left">Atidaryta</th>
                <th className="text-left">Būsena</th>
                <th className="text-left">Šaltinis</th>
                <th className="text-right">Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {workspaces?.map((ws: WorkspaceInfo) => (
                <Fragment key={ws.id}>
                <tr className={`border-b border-gray-50 ${ws.isActive ? 'bg-primary-50/50' : ''} ${(ws as WorkspaceInfo & { hidden?: boolean }).hidden ? 'opacity-50' : ''}`}>
                  <td className="font-medium">
                    <div className={((ws as WorkspaceInfo & { hidden?: boolean }).hidden ?? false) ? 'line-through text-gray-400' : ''}>{ws.name}</div>
                    {/* Owner from the shop's registry — login-independent (owner rule
                        2026-09-11). Owner only; membership count is noise here. */}
                    {ws.ownerEmail && (
                      <div className="text-xs text-gray-400 truncate max-w-[220px]">
                        {ws.ownerEmail}
                      </div>
                    )}
                  </td>
                  <td className="text-gray-500">{formatDate(ws.createdAt)}</td>
                  <td className="text-gray-500">{formatDate(ws.lastOpenedAt)}</td>
                  <td>
                    {ws.isActive ? (
                      <span className="text-xs font-medium text-green-600">Aktyvi</span>
                    ) : (
                      <span className="text-xs text-gray-400">Neaktyvi</span>
                    )}
                  </td>
                  <td className="text-xs">
                    {ws.sourceKind === 'mysql' ? (
                      <div>
                        <span className="font-medium text-primary-700">☁ sinchronizuojama</span>
                        <div className="text-gray-400">
                          {ws.lastSyncedAtUtc
                            ? `Atnaujinta: ${new Date(ws.lastSyncedAtUtc).toLocaleString('lt-LT')}`
                            : 'Dar nesinchronizuota'}
                        </div>
                        {/* ADR-012: write-back state chip */}
                        <div className={`mt-0.5 ${ws.writebackConfigured ? 'text-green-600' : 'text-gray-400'}`}>
                          {ws.writebackConfigured
                            ? `✎ registravimas įjungtas (${ws.writebackSlug})`
                            : 'registravimas išjungtas'}
                        </div>
                        <button
                          className="text-primary-600 hover:underline mt-0.5"
                          onClick={() => handleSyncNow(ws.id)}
                          disabled={syncingId === ws.id}
                        >
                          {syncingId === ws.id ? 'Sinchronizuojama…' : 'Sinchronizuoti dabar'}
                        </button>
                      </div>
                    ) : (
                      // Cloud databases are linked automatically at sign-in
                      // (CloudWorkspaceProvisioner) — there is no manual linking.
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="text-right space-x-2">
                    {!ws.isActive && (
                      <button
                        className="text-xs text-primary-600 hover:underline"
                        onClick={() => activateMutation.mutate(ws.id)}
                        disabled={activateMutation.isPending}
                      >
                        Aktyvuoti
                      </button>
                    )}
                    {ws.sourceKind === 'mysql' && (
                      <button
                        className={`text-xs ${ws.writebackConfigured ? 'text-green-600 hover:text-green-700' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => (writebackEditId === ws.id ? setWritebackEditId(null) : openWritebackEditor(ws))}
                      >
                        {writebackEditId === ws.id ? 'Uždaryti' : 'Registravimas'}
                      </button>
                    )}
                    <button
                      className={`text-xs ${ws.aiEgressDisabled ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                      onClick={() => toggleAiMutation.mutate({ id: ws.id, disabled: !ws.aiEgressDisabled })}
                      disabled={toggleAiMutation.isPending}
                      title={ws.aiEgressDisabled ? 'AI išjungtas — spustelėkite, kad įjungtumėte' : 'AI įjungtas — spustelėkite, kad išjungtumėte'}
                    >
                      {ws.aiEgressDisabled ? '🔒 AI Off' : '🤖 AI On'}
                    </button>
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => handleExport(ws)}
                    >
                      Eksportuoti
                    </button>
                    {!ws.isActive && !ws.hidden && (
                      <button
                        className="text-xs text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(ws)}
                        disabled={deleteMutation.isPending}
                      >
                        Pašalinti
                      </button>
                    )}
                    {ws.hidden && (
                      <button
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                        onClick={() => restoreMutation.mutate(ws.id)}
                        disabled={restoreMutation.isPending}
                      >
                        Atkurti
                      </button>
                    )}
                  </td>
                </tr>
                {writebackEditId === ws.id && (
                  <tr className="border-b border-gray-50 bg-gray-50/70">
                    <td colSpan={6} className="px-4 py-3">
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Serviso slugas</div>
                          <input
                            type="text"
                            className="w-40 px-2 py-1 text-sm border border-gray-300 rounded-md"
                            placeholder="pvz. mbintegrav"
                            value={writebackForm.slug}
                            onChange={(e) => setWritebackForm(f => ({ ...f, slug: e.target.value }))}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Programos vardas</div>
                          <input
                            type="text"
                            className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-md"
                            value={writebackForm.appName}
                            onChange={(e) => setWritebackForm(f => ({ ...f, appName: e.target.value }))}
                          />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Prieigos raktas</div>
                          <input
                            type="password"
                            className="w-64 px-2 py-1 text-sm border border-gray-300 rounded-md"
                            placeholder={ws.writebackConfigured ? '(nekeičiamas)' : 'įklijuokite raktą'}
                            value={writebackForm.token}
                            onChange={(e) => setWritebackForm(f => ({ ...f, token: e.target.value }))}
                          />
                        </div>
                        <button
                          className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                          disabled={setWritebackMutation.isPending || !writebackForm.slug.trim() || (!ws.writebackConfigured && !writebackForm.token.trim())}
                          onClick={() => setWritebackMutation.mutate({ id: ws.id, data: writebackForm })}
                        >
                          {setWritebackMutation.isPending ? 'Išsaugoma…' : 'Įrašyti'}
                        </button>
                        {ws.writebackConfigured && (
                          <button
                            className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50"
                            disabled={setWritebackMutation.isPending}
                            onClick={() => handleDisableWriteback(ws.id)}
                          >
                            Išjungti
                          </button>
                        )}
                        <span className="text-xs text-gray-400">
                          Įjungus, sąskaitų juodraščius bus galima registruoti tiesiai į kliento Apskaita5 knygą.
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
              {(!workspaces || workspaces.length === 0) && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 py-4">
                    Darbo sričių dar nėra — kliento įmonės darbo sritis atsiranda automatiškai, kai jos naudotojas prisijungia per integravizija.lt. Galite sukurti naują ir rankiniu būdu aukščiau.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

